import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Headphones } from "lucide-react";
import KineticHero from "@/components/KineticHero";
import EditorialMarquee from "@/components/EditorialMarquee";
import ArticleSection from "@/components/ArticleSection";
import ThreadPanel from "@/components/ThreadPanel";
import AuthModal from "@/components/AuthModal";
import FloatingAudioPlayer from "@/components/FloatingAudioPlayer";
import { articles as staticArticles } from "@/data/articles";
import { useAuth } from "@/hooks/useAuth";
import { APP_VERSION, PUBLISHED_ON } from "@/version";

const EASE = [0.76, 0, 0.24, 1];

export default function Home() {
    const { user, logout } = useAuth();
    const [authOpen, setAuthOpen] = useState(false);
    const [authMode, setAuthMode] = useState("login");
    const [articles, setArticles] = useState(staticArticles);
    const [playerOpen, setPlayerOpen] = useState(false);
    const [autoplayNonce, setAutoplayNonce] = useState(0);
    const [hero, setHero] = useState(null);

    // Fetch hero content from the CMS. Chapters still exist in the CMS but
    // are not rendered on the public page — the Section I overview replaces
    // the standalone chapters block.
    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const r = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/site/home`,
                );
                if (cancel) return;
                if (r.data?.hero) setHero(r.data.hero);
            } catch {
                // fall back to hardcoded defaults
            }
        })();
        return () => {
            cancel = true;
        };
    }, []);

    const startPodcast = (e) => {
        if (e && typeof e.preventDefault === "function") e.preventDefault();
        setPlayerOpen(true);
        setAutoplayNonce((n) => n + 1);
    };

    // Global event bridge so the nav "Listen" link (and anywhere else) can
    // open/play the podcast player from anywhere on the page.
    useEffect(() => {
        const play = () => {
            setPlayerOpen(true);
            setAutoplayNonce((n) => n + 1);
        };
        const show = () => setPlayerOpen(true);
        // HelpCTA fires this to open the sign-up modal from anywhere on the
        // page (buttons on the podcast + article threads + Article III tail).
        const openAuthEvt = (e) => {
            const mode = e?.detail?.mode === "login" ? "login" : "register";
            setAuthMode(mode);
            setAuthOpen(true);
        };
        window.addEventListener("en:play-audio", play);
        window.addEventListener("en:show-audio", show);
        window.addEventListener("en:open-auth", openAuthEvt);
        return () => {
            window.removeEventListener("en:play-audio", play);
            window.removeEventListener("en:show-audio", show);
            window.removeEventListener("en:open-auth", openAuthEvt);
        };
    }, []);

    // PIPEDA-friendly visitor ping — once per browser session.
    useEffect(() => {
        try {
            if (sessionStorage.getItem("en_tracked")) return;
            axios
                .post(`${process.env.REACT_APP_BACKEND_URL}/api/track/visit`)
                .then(() => sessionStorage.setItem("en_tracked", "1"))
                .catch(() => {});
        } catch (e) {
            // best-effort tracking — don't disturb the page on failure
        }
    }, []);

    // Fetch published articles from CMS (already sorted by `order`).
    // For each returned article, if body_html is present it wins; otherwise
    // the frontend falls back to the structured static content by slug.
    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const list = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/articles`,
                );
                if (cancel || !Array.isArray(list.data) || list.data.length === 0) return;
                const staticBySlug = new Map(staticArticles.map((a) => [a.slug, a]));
                const withBodies = await Promise.all(
                    list.data.map(async (a) => {
                        try {
                            const r = await axios.get(
                                `${process.env.REACT_APP_BACKEND_URL}/api/articles/${a.slug}`,
                            );
                            return r.data;
                        } catch {
                            return a;
                        }
                    }),
                );
                if (cancel) return;
                const merged = withBodies.map((d) => {
                    const s = staticBySlug.get(d.slug) || {};
                    return {
                        ...s,
                        ...d,
                        threadId: d.thread_id || s.threadId || d.slug,
                        readMinutes: d.read_minutes ?? s.readMinutes,
                        pdfUrl: d.pdf_url || s.pdfUrl || "",
                        pdfFilename: d.pdf_filename || s.pdfFilename || `${d.title || "article"}.pdf`,
                        // Prefer authored HTML; only fall back to structured sections when no HTML.
                        sections: d.body_html ? null : s.sections || null,
                    };
                });
                setArticles(merged);
            } catch {
                // API unavailable — keep static articles
            }
        })();
        return () => {
            cancel = true;
        };
    }, []);

    const openAuth = (mode) => {
        setAuthMode(mode);
        setAuthOpen(true);
    };

    return (
        <div
            data-testid="home-page"
            className="relative min-h-screen w-full text-[color:var(--charcoal)] pb-32"
        >
            {/* Nav */}
            <nav
                data-testid="site-nav"
                className="fixed top-0 left-0 right-0 z-40 backdrop-blur-md border-b border-[color:var(--charcoal)]/10"
                style={{ background: "rgba(226, 238, 248, 0.9)" }}
            >
                <div className="mx-auto max-w-[1440px] px-4 sm:px-6 md:px-10 py-4 md:py-5 flex items-center justify-between gap-3 text-[color:var(--charcoal)]">
                    <a
                        href="#top"
                        data-testid="brand-mark"
                        className="font-display italic text-lg md:text-xl tracking-tight shrink-0"
                    >
                        EquityNation
                        <span style={{ opacity: 0.55 }}>.ca</span>
                    </a>
                    <div className="hidden sm:flex items-center gap-3 sm:gap-4 md:gap-8 font-mono font-semibold text-[11px] md:text-[12px] uppercase tracking-[0.14em] md:tracking-[0.24em]">
                        <a href="#article-the-equity-nation" data-testid="nav-manifesto">
                            Manifesto
                        </a>
                        <a href="#documents" data-testid="nav-documents">
                            Our Economy
                        </a>
                        <a
                            href="#audio"
                            data-testid="nav-audio"
                            onClick={() =>
                                window.dispatchEvent(
                                    new CustomEvent("en:show-audio"),
                                )
                            }
                        >
                            Podcast
                        </a>
                    </div>
                    <div className="font-mono font-semibold text-[11px] md:text-[12px] uppercase tracking-[0.1em] sm:tracking-[0.14em] md:tracking-[0.24em] flex items-center gap-2 sm:gap-3 md:gap-4 text-[color:var(--slate)] whitespace-nowrap shrink-0" style={{ display: "flex" }}>
                        {user ? (
                            <>
                                <a
                                    href="/account"
                                    data-testid="nav-user-nick"
                                    className="hover:text-[color:var(--charcoal)]"
                                >
                                    Hi, {user.nickname || user.name}
                                </a>
                                {(user.role === "moderator" || user.role === "admin" || user.role === "super_admin") && (
                                    <a href="/admin" data-testid="nav-admin" className="hover:text-[color:var(--charcoal)]">Admin</a>
                                )}
                                <button type="button" onClick={logout} data-testid="nav-logout" className="hover:text-[color:var(--charcoal)]">Sign out</button>
                            </>
                        ) : (
                            <>
                                <button type="button" onClick={() => openAuth("login")} data-testid="nav-signin" className="hover:text-[color:var(--charcoal)]">Sign in</button>
                                <button type="button" onClick={() => openAuth("register")} data-testid="nav-register" className="hover:text-[color:var(--charcoal)]">Join</button>
                            </>
                        )}
                    </div>
                </div>
            </nav>

            <div id="top" />

            {/* Hero */}
            <KineticHero hero={hero} />

            {/* Editorial marquee */}
            <EditorialMarquee />

            {/* Podcast — sits right under the banner for immediate access */}
            <section
                id="audio"
                data-testid="audio-section"
                className="mx-auto max-w-[1440px] px-6 md:px-10 py-14 md:py-20"
            >
                <div className="grid grid-cols-12 gap-6 md:gap-10 items-start">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 1, ease: EASE }}
                        className="col-span-12 md:col-span-7"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <span className="inline-block h-px w-10 bg-[color:var(--charcoal)]" />
                            <span className="font-mono font-semibold text-[12px] uppercase tracking-[0.28em]">
                                Section I · Listen
                            </span>
                        </div>
                        <h2 className="font-display text-[2rem] md:text-[3.375rem] lg:text-[4rem] leading-[0.95] tracking-[-0.02em] max-w-[18ch]">
                            The Monopoly Podcast — Brief Summary.
                        </h2>

                        <div className="mt-8 md:mt-10 flex flex-col items-start gap-4">
                            {!playerOpen && (
                                <button
                                    type="button"
                                    onClick={startPodcast}
                                    data-testid="podcast-cta"
                                    className="pill-btn w-full sm:w-auto justify-center"
                                    style={{
                                        padding: "1.05rem 2rem",
                                        fontSize: "0.78rem",
                                        letterSpacing: "0.2em",
                                    }}
                                >
                                    <Headphones size={16} strokeWidth={1.5} />
                                    Listen to our introductory podcast
                                </button>
                            )}
                            <FloatingAudioPlayer
                                src="/assets/podcast_monopoly.mp3"
                                title="Monopoly"
                                episode="EP · 01"
                                open={playerOpen}
                                autoplayNonce={autoplayNonce}
                                onClose={() => setPlayerOpen(false)}
                            />
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 1, ease: EASE, delay: 0.15 }}
                        className="col-span-12 md:col-span-4 md:col-start-9 md:pt-24"
                    >
                        <p className="font-mono font-semibold text-[12px] uppercase tracking-[0.24em] mb-3 text-[color:var(--slate)]">
                            Description
                        </p>
                        <p className="font-body text-base md:text-lg leading-[1.6] text-[color:var(--charcoal)]/85">
                            A brief podcast about who really controls the board
                            now — and how Canadians can take control.
                        </p>
                    </motion.div>
                </div>

                {/* Consolidated overview — the "read while you listen" summary
                    the customer supplied. Editorial serif with headings, italic
                    lead-ins, and pull-quotes matched to the site typography. */}
                <div
                    data-testid="podcast-overview"
                    className="mt-12 md:mt-16 border-t border-[color:var(--charcoal)]/15 pt-10 md:pt-14"
                >
                    <div className="grid grid-cols-12 gap-6 md:gap-10">
                        <div className="col-span-12 md:col-span-8 md:col-start-3 max-w-[68ch]">
                            <h3 className="font-display text-[1.5rem] md:text-[1.9rem] leading-tight tracking-[-0.01em] mb-5">
                                A logical approach, presented in plain Canadian.
                            </h3>

                            <p className="font-body text-base md:text-lg leading-[1.65] text-[color:var(--charcoal)]/90 mb-8">
                                <em className="italic">The Equity Nation</em>{" "}
                                lays out the concept, the evidence, and the
                                case for an equity-first Canada. The rational
                                originating talk to Social Credit supporters,
                                the Equity Nation manifesto, and the audio
                                summary of benefits only work if discussed
                                around kitchen tables, in classrooms, and with
                                our Members of Parliament.
                            </p>

                            <h3 className="font-display text-[1.5rem] md:text-[1.9rem] leading-tight tracking-[-0.01em] mb-3">
                                An economy is not weather. It is a choice.
                            </h3>
                            <p className="font-display italic text-lg md:text-xl leading-snug text-[color:var(--charcoal)]/95 mb-4 border-l-2 border-[color:var(--charcoal)] pl-4">
                                &ldquo;Canada was never destined to be a
                                tenant nation. We chose that. We can choose
                                again.&rdquo;
                            </p>
                            <p className="font-body text-base md:text-lg leading-[1.65] text-[color:var(--charcoal)]/90 mb-10">
                                For generations, Canadians have been told
                                that the rules of the market are fixed —
                                that housing prices, precarious work, and
                                shrinking public services are simply the
                                climate of a modern economy. They are not.
                                They are policies. They are choices.
                            </p>

                            <h3 className="font-display text-[1.5rem] md:text-[1.9rem] leading-tight tracking-[-0.01em] mb-3">
                                Canada&rsquo;s national and natural resources
                                are your missing paycheck.
                            </h3>
                            <p className="font-body text-base md:text-lg leading-[1.65] text-[color:var(--charcoal)]/90">
                                <em className="italic font-display">
                                    &ldquo;Wages tell you what a worker is
                                    worth this week. Government equity tells
                                    you what Canada is worth for you every
                                    day.&rdquo;
                                </em>{" "}
                                Wages are a rental agreement. Equity is a
                                deed. The imbalance between the two is the
                                quiet reason Canadian households feel like
                                they are running to stand still — the
                                economy grows, but the equity accumulates
                                elsewhere. This manifesto proposes a
                                straightforward reweighting to broaden the
                                base of ownership so that productivity gains
                                actually reach the people producing them.
                                Not charity. Not redistribution.{" "}
                                <strong className="font-semibold">
                                    Rational structure.
                                </strong>
                            </p>
                        </div>
                    </div>

                    {/* Podcast discussion — sits directly under the overview
                        so listeners can weigh in while the audio is still in
                        their head. */}
                    <div data-testid="podcast-discussion" className="mt-2">
                        <ThreadPanel
                            threadId="podcast-monopoly"
                            label="Monopoly — the podcast"
                            testId="thread-podcast-monopoly"
                        />
                    </div>
                </div>
            </section>

            {/* Articles — inline editorial reading experience */}
            <section id="documents" data-testid="articles-section">
                {articles.map((article) => (
                    <ArticleSection
                        key={article.slug}
                        article={article}
                        canEdit={user?.role === "admin" || user?.role === "super_admin"}
                    />
                ))}
            </section>

            {/* Footer */}
            <footer
                data-testid="site-footer"
                className="border-t border-[color:var(--charcoal)]/25 mt-4"
            >
                <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-12 md:py-16 grid grid-cols-12 gap-6 md:gap-10">
                    <div className="col-span-12 md:col-span-8">
                        <p className="font-display italic text-[2rem] md:text-[3.375rem] leading-[0.95] tracking-[-0.02em] max-w-[14ch]">
                            An economy owned by the people who power it.
                        </p>
                    </div>
                    <div className="col-span-12 md:col-span-4 flex flex-col gap-2 font-mono text-[12px] uppercase tracking-[0.24em]">
                        <span className="text-[color:var(--slate)]">
                            Contents
                        </span>
                        <a href="#article-the-equity-nation">Manifesto</a>
                        <a href="#documents">Our Economy</a>
                        <a href="#audio">Podcast</a>
                    </div>
                    <div className="col-span-12 pt-8 mt-2 border-t border-[color:var(--charcoal)]/20 flex flex-wrap items-center justify-between gap-3 font-mono text-[12px] uppercase tracking-[0.24em] text-[color:var(--slate)]">
                        <span>© 2025 The Equity Nation</span>
                        <span>A logical approach to economics for Canada</span>
                        <span
                            data-testid="site-version"
                            title={`Deployed ${PUBLISHED_ON}`}
                        >
                            v{APP_VERSION} · {PUBLISHED_ON}
                        </span>
                    </div>
                </div>
            </footer>
            <AuthModal open={authOpen} mode={authMode} onClose={() => setAuthOpen(false)} />
        </div>
    );
}
